// Shared "Management & review" add-on helpers for Post a Project (Atrium Stage 7).
//
// These compute the AddonStep props + the add-on price contribution. The
// managed_services_tier / quality_assurance_addon / quality_control_enabled /
// managed_service_details fields are JOB-LEVEL singletons on the parent
// job_postings row (one per project, not per service) — CONFIG-SCHEMA §5.
//
// Source of truth = the Rails backend:
//   tiers        app/models/job_postings/job_posting.rb (enum managed_services_tier
//                = { self_managed: 0, managed: 3 } — supported/guided are retired)
//   gating       job_posting.rb#available_service_tiers (managed dropped for SEO < 10
//                articles, plagiarism, illustration)
//   eligibility  base_posting.rb#eligible_for_qa_qc? (book_writing, book_description, seo, humanize_ai)
//   qa/qc cost   base_posting.rb#quality_assurance_cost / #quality_control_cost
//   managed cost base_posting.rb#managed_service_cost + managed_service.rb
//                (MANUSCRIPT_HOURLY_PRICES / NON_MANUSCRIPT_PRICES / SEO_PRICING),
//                $20/hr from OrderPayment::MANAGED_SERVICES[:hourly], $20 floor
//
// The AddonStep component renders the managed questionnaire from
// options.QUESTIONNAIRE_OPTIONS; this module only supplies tiers + price math. The
// server /post/price endpoint is authoritative — these are rail estimates.

import { MANAGED_TIERS, RATES } from "./options"

// Services eligible for QA/QC add-ons (base_posting.rb#eligible_for_qa_qc?).
// SEO removed (Plumbline Phase 0, ruling C8) — it no longer auto-includes QA/QC.
export const ELIGIBLE_QA_QC = ["book_writing", "book_description", "humanize_ai"]

// QA/QC enum strings (job_posting.rb enum quality_assurance_addon /
// quality_control_enabled = { declined: 0, selected: 1, included: 2 }).
export const QA_DECLINED = "declined"
export const QA_SELECTED = "selected"
const QA_INCLUDED = "included"

// Managed-services pricing internals for the `managed` tier — the only purchasable
// managed tier besides self_managed. Sourced from managed_service.rb (NOT options.js:
// these are managed-service internals, not enum values). $20/hr base; $20 floor.
const MS_HOURLY = 20.0
const MS_FLOOR = 20.0

// MANUSCRIPT_HOURLY_PRICES['managed'] (words per managed-service hour), by service key.
const MANAGED_MANUSCRIPT_HOURLY_WORDS = {
  book_writing: 1500,
  editing: 3750,
  narration: 2000,
  content_translation: 3750,
  plagiarism: 3750,
  humanize_ai: 1500,
}

// NON_MANUSCRIPT_PRICES['managed'] (managed-service hours), by service key.
const MANAGED_NON_MANUSCRIPT_HOURS = {
  outline: 3.0,
  book_description: 2.5,
  cover_design: 2.5,
  formatting: 1.0,
  illustration: 2.5,
}

// SEO_PRICING['managed'] — price per (up to 30-article) pack.
const MANAGED_SEO_PACK_PRICE = 80.0

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

// ── word-count resolution ─────────────────────────────────────────────────────

// The QA/QC and manuscript managed-service formulas key off a posting's word
// count. book_description is fixed at 500; sample at 1,000; everything else reads
// the live word_count from state.
function wordCountFor(state, svc) {
  const sub = (state.postings && state.postings[svc]) || {}
  if (svc === "book_description") return RATES.BOOK_DESCRIPTION_WORD_COUNT
  if (svc === "sample") return RATES.SAMPLE_WORD_COUNT
  return Number(sub.word_count) || 0
}

// ── eligibility / gating ──────────────────────────────────────────────────────

export function isQaQcEligible(selected = []) {
  return (selected || []).some((k) => ELIGIBLE_QA_QC.includes(k))
}

// Managed is unavailable when any selected service forbids it (available_service_tiers):
// SEO with < 10 articles, plagiarism, or illustration.
export function managedAllowed(selected = [], state = {}) {
  const list = selected || []

  if (list.includes("plagiarism") || list.includes("illustration")) return false

  if (list.includes("seo")) {
    const seo = (state.postings && state.postings.seo) || {}
    const articles = Number(seo.article_count)
    if (Number.isFinite(articles) && articles < 10) return false
  }

  return true
}

// Whether the "Management & review" stage exists at all (AU-26). It is a real
// step only when the customer is managed-services flagged AND the selection
// leaves something to decide there — Managed available, or a QA/QC add-on
// addable. One predicate drives render, navigation, hydration and review so a
// draft cursor can never land on a suppressed stage.
export function hasAddonChoice(managedServicesFlag, selected = [], state = {}) {
  if (!managedServicesFlag) return false
  return managedAllowed(selected, state) || isQaQcEligible(selected)
}

// ── managed-service cost ──────────────────────────────────────────────────────

// One posting's managed-service cost to the customer (managed only; self_managed
// is not "managed_services_enabled?" so it is $0). Mirrors managed_service_cost +
// ManagedService.{manuscript,non_manuscript,seo}_prices with the $20 floor.
function managedServiceCostFor(svc, state, tier) {
  if (tier !== "managed") return 0

  let cost = 0

  if (svc === "seo") {
    const seo = (state.postings && state.postings.seo) || {}
    const articles = Number(seo.article_count) || 0
    const packs = Math.floor(articles / 31) + 1
    cost = MANAGED_SEO_PACK_PRICE * packs
  } else if (Object.prototype.hasOwnProperty.call(MANAGED_MANUSCRIPT_HOURLY_WORDS, svc)) {
    const wc = wordCountFor(state, svc)
    cost = MS_HOURLY * (wc / MANAGED_MANUSCRIPT_HOURLY_WORDS[svc])
  } else if (Object.prototype.hasOwnProperty.call(MANAGED_NON_MANUSCRIPT_HOURS, svc)) {
    cost = MS_HOURLY * MANAGED_NON_MANUSCRIPT_HOURS[svc]
  } else {
    // sample / out_of_scope — no managed pricing table → $0.
    return 0
  }

  cost = round2(cost)
  if (cost === 0) return 0
  return Math.max(cost, MS_FLOOR)
}

function managedServicesTotal(selected, state, tier) {
  return round2(
    (selected || []).reduce((sum, svc) => sum + managedServiceCostFor(svc, state, tier), 0)
  )
}

// The TWO managed tiers (self_managed / managed) with their resolved "from" floor.
// managed "from" = the computed managed-services total for the current selection,
// never below the $20 floor. Best-effort; the server price is authoritative.
export function managedTiersFor(selected = [], state = {}, _ctx = {}) {
  const managedTotal = managedServicesTotal(selected, state, "managed")

  return MANAGED_TIERS.map((t) => {
    if (t.value === "managed") {
      return { ...t, from: Math.max(managedTotal, MS_FLOOR) }
    }
    return { ...t }
  })
}

// ── QA / QC cost ──────────────────────────────────────────────────────────────

// The QA/QC base / per-thousand / QC rates are PriceList-driven and Insider-aware
// (base_posting.rb resolves them via price_list.rate_for(:addon_cost, …)), so the
// displayed QA/QC card + rail amounts read the server-resolved ctx.rates.addon_cost
// map with the RATES.ADDON_COST seed as the no-live-PriceList fallback — never a
// hardcoded literal (Plumbline WI-A10 / WI-C2, tuw-app:P-001).
function addonRates(ctx = {}) {
  const seed = RATES.ADDON_COST
  const r = (ctx.rates && ctx.rates.addon_cost) || {}
  return {
    qa_base: r.qa_base ?? seed.qa_base,
    qa_per_thousand: r.qa_per_thousand ?? seed.qa_per_thousand,
    quality_control: r.quality_control ?? seed.quality_control,
  }
}

// QA = $225 base for <= 50k words, then +$2.25 per 1,000 words beyond. Exact float.
function qaCostFor(wordCount, ctx = {}) {
  const rates = addonRates(ctx)
  const thousands = (Number(wordCount) || 0) / 1000
  const base = rates.qa_base
  if (thousands < 50) return base
  return base + (thousands - 50) * rates.qa_per_thousand
}

// QC = $10 per 1,000 words. Exact float.
function qcCostFor(wordCount, ctx = {}) {
  return ((Number(wordCount) || 0) / 1000) * addonRates(ctx).quality_control
}

// Aggregate QA / QC amounts across the eligible selected services (for the
// AddonStep price hints — regardless of whether QA/QC is currently selected).
export function addonLineAmounts(state = {}, selected = [], ctx = {}) {
  const eligible = (selected || []).filter((k) => ELIGIBLE_QA_QC.includes(k))

  let qa = 0
  let qc = 0
  for (const svc of eligible) {
    const wc = wordCountFor(state, svc)
    qa += qaCostFor(wc, ctx)
    qc += qcCostFor(wc, ctx)
  }

  return { qa: round2(qa), qc: round2(qc) }
}

// ── price-rail contribution ───────────────────────────────────────────────────

// The add-on lines that flow into the PriceRail: QA + QC (only when eligible AND
// enabled, or auto-included by managed) + the managed-services total (managed).
export function addonEstimate(state = {}, selected = [], ctx = {}) {
  const lines = []
  const tier = state.managed_services_tier || "self_managed"
  const managed = tier === "managed"
  const eligible = (selected || []).filter((k) => ELIGIBLE_QA_QC.includes(k))

  const qaOn =
    managed ||
    state.quality_assurance_addon === QA_SELECTED ||
    state.quality_assurance_addon === QA_INCLUDED
  const qcOn =
    managed ||
    state.quality_control_enabled === QA_SELECTED ||
    state.quality_control_enabled === QA_INCLUDED

  let qa = 0
  let qc = 0
  for (const svc of eligible) {
    const wc = wordCountFor(state, svc)
    if (qaOn) qa += qaCostFor(wc, ctx)
    if (qcOn) qc += qcCostFor(wc, ctx)
  }
  qa = round2(qa)
  qc = round2(qc)

  if (qaOn && qa > 0) lines.push({ label: "Quality Assurance", amount: qa })
  if (qcOn && qc > 0) lines.push({ label: "Quality Control", amount: qc })

  if (managed) {
    const ms = managedServicesTotal(selected, state, "managed")
    if (ms > 0) lines.push({ label: "Managed Services", amount: ms })
  }

  const total = round2(lines.reduce((sum, l) => sum + l.amount, 0))

  return { lines, total }
}

// ── /post/price bridge ────────────────────────────────────────────────────────

// The job-level fields PostingCalculator reads off EACH project entry (it runs
// managed_service_qa_qc_params per posting). The page must spread these into every
// project's pricePayload before POST /post/price, because the per-service
// pricePayload only carries that service's own cost_fields. The calculator wants
// managed_services_tier as an INTEGER (0/3) and quality_assurance/quality_control
// as 'true'/'false'; managed forces both QA and QC on server-side.
export function addonPriceFields(state = {}) {
  const managed = (state.managed_services_tier || "self_managed") === "managed"

  const qa =
    managed ||
    state.quality_assurance_addon === QA_SELECTED ||
    state.quality_assurance_addon === QA_INCLUDED
  const qc =
    managed ||
    state.quality_control_enabled === QA_SELECTED ||
    state.quality_control_enabled === QA_INCLUDED

  return {
    managedServicesTier: managed ? 3 : 0,
    qualityAssurance: qa ? "true" : "false",
    qualityControl: qc ? "true" : "false",
  }
}
